/**
 * Custom hook for clause notifications
 * Handles urgent clauses and attention-required notifications
 */

import { useCallback, useEffect } from 'react';
import { toast } from 'sonner';

import { ClauseProperties } from '@/utils/clauseIdUtils';
import { useClauseState } from './useClauseState';

export interface NotificationConfig {
  showUrgentNotifications: boolean;
  showAttentionNotifications: boolean;
  showStatusChangeNotifications: boolean;
  showContractRatificationNotifications: boolean;
}

export const useClauseNotifications = (
  contractId: string,
  userRole: 'buyer' | 'seller' | 'agent',
  contractRatified: boolean = false,
  config: NotificationConfig = {
    showUrgentNotifications: true,
    showAttentionNotifications: true,
    showStatusChangeNotifications: true,
    showContractRatificationNotifications: true,
  }
) => {
  const {
    urgentClauses,
    attentionClauses,
    negotiationSummary,
    hasPendingNegotiations,
  } = useClauseState({
    contractId,
    userRole,
    contractRatified,
  });

  // Show urgent clause notifications
  useEffect(() => {
    if (config.showUrgentNotifications && urgentClauses.length > 0) {
      urgentClauses.forEach((clause) => {
        toast.error(`Urgent Action Required: ${clause.clauseId}`, {
          description: 'This clause requires immediate attention',
          duration: 10000,
          action: {
            label: 'View Clause',
            onClick: () => {
              // Navigate to clause or scroll to it
              const element = document.getElementById(
                `clause-${clause.hierarchicalId}`
              );
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            },
          },
        });
      });
    }
  }, [urgentClauses, config.showUrgentNotifications]);

  // Show attention clause notifications
  useEffect(() => {
    if (config.showAttentionNotifications && attentionClauses.length > 0) {
      attentionClauses.forEach((clause) => {
        toast.warning(`Attention Required: ${clause.clauseId}`, {
          description: 'This clause requires your attention',
          duration: 8000,
          action: {
            label: 'View Clause',
            onClick: () => {
              const element = document.getElementById(
                `clause-${clause.hierarchicalId}`
              );
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            },
          },
        });
      });
    }
  }, [attentionClauses, config.showAttentionNotifications]);

  // Show contract ratification notifications
  useEffect(() => {
    if (config.showContractRatificationNotifications && contractRatified) {
      toast.info('Contract Ratified', {
        description:
          'This contract is now ratified. Only addendums, extensions, and repair requests can be negotiated.',
        duration: 5000,
      });
    }
  }, [contractRatified, config.showContractRatificationNotifications]);

  // Show negotiation summary notifications
  useEffect(() => {
    if (negotiationSummary && hasPendingNegotiations) {
      const { pendingClauses, urgentClauses: urgentCount } = negotiationSummary;

      if (urgentCount > 0) {
        toast.error(`${urgentCount} Urgent Clauses`, {
          description: `${pendingClauses} total clauses pending negotiation`,
          duration: 6000,
        });
      } else if (pendingClauses > 0) {
        toast.warning(`${pendingClauses} Clauses Pending`, {
          description: 'You have clauses that require your attention',
          duration: 5000,
        });
      }
    }
  }, [negotiationSummary, hasPendingNegotiations]);

  // Custom notification methods
  const showClauseStatusChangeNotification = useCallback(
    (clause: ClauseProperties, oldStatus: string, newStatus: string) => {
      if (!config.showStatusChangeNotifications) return;

      const statusMessages = {
        original: 'Original',
        proposed: 'Proposed',
        countered: 'Countered',
        accepted: 'Accepted',
        rejected: 'Rejected',
        ratified: 'Ratified',
      };

      const oldStatusText =
        statusMessages[oldStatus as keyof typeof statusMessages] || oldStatus;
      const newStatusText =
        statusMessages[newStatus as keyof typeof statusMessages] || newStatus;

      toast.success(`Clause Status Updated: ${clause.clauseId}`, {
        description: `Status changed from ${oldStatusText} to ${newStatusText}`,
        duration: 4000,
      });
    },
    [config.showStatusChangeNotifications]
  );

  const showClauseActionNotification = useCallback(
    (
      clause: ClauseProperties,
      action: 'countered' | 'accepted' | 'rejected' | 'edited'
    ) => {
      const actionMessages = {
        countered: 'Clause Countered',
        accepted: 'Clause Accepted',
        rejected: 'Clause Rejected',
        edited: 'Clause Edited',
      };

      const actionText = actionMessages[action];
      const isPositive = action === 'accepted';
      const isNegative = action === 'rejected';

      if (isPositive) {
        toast.success(`${actionText}: ${clause.clauseId}`, {
          description: 'This clause has been accepted',
          duration: 4000,
        });
      } else if (isNegative) {
        toast.error(`${actionText}: ${clause.clauseId}`, {
          description: 'This clause has been rejected',
          duration: 4000,
        });
      } else {
        toast.info(`${actionText}: ${clause.clauseId}`, {
          description: `This clause has been ${action}`,
          duration: 4000,
        });
      }
    },
    []
  );

  const showContractStatusNotification = useCallback(
    (status: 'ratified' | 'pending' | 'expired' | 'cancelled') => {
      const statusMessages = {
        ratified: 'Contract Ratified',
        pending: 'Contract Pending',
        expired: 'Contract Expired',
        cancelled: 'Contract Cancelled',
      };

      const statusText = statusMessages[status];
      const isPositive = status === 'ratified';
      const isNegative = status === 'expired' || status === 'cancelled';

      if (isPositive) {
        toast.success(statusText, {
          description: 'The contract has been successfully ratified',
          duration: 5000,
        });
      } else if (isNegative) {
        toast.error(statusText, {
          description: `The contract status is now ${status}`,
          duration: 5000,
        });
      } else {
        toast.info(statusText, {
          description: `The contract status is now ${status}`,
          duration: 4000,
        });
      }
    },
    []
  );

  const showSpecialCircumstanceNotification = useCallback(
    (type: 'addendum' | 'extension' | 'repair_request', clauseId: string) => {
      const typeMessages = {
        addendum: 'Addendum Created',
        extension: 'Extension Requested',
        repair_request: 'Repair Request Created',
      };

      const typeText = typeMessages[type];

      toast.info(`${typeText}: ${clauseId}`, {
        description: `A ${type.replace(
          '_',
          ' '
        )} has been created for this clause`,
        duration: 5000,
      });
    },
    []
  );

  return {
    showClauseStatusChangeNotification,
    showClauseActionNotification,
    showContractStatusNotification,
    showSpecialCircumstanceNotification,
  };
};
